import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";
import Chart from "chart.js/auto";

const timeLabels = [
  "07:00", "08:00", "09:00", "10:00",
  "11:00", "12:00", "13:00", "14:00"
];

const chartParams = [
  "Systolic BP (mmHg)",
  "Diastolic BP (mmHg)",
  "Heart Rate (bpm)",
  "CVP (cmH2O)",
  "Respiratory Rate",
  "Temperature (°C)"
];

const allParams = [
  ...chartParams,
  "Operation Phase",
  "Additional Notes"
];

// Suggested values for autofill
const paramSuggestions = {
  "Systolic BP (mmHg)": ["90", "100", "120", "140"],
  "Diastolic BP (mmHg)": ["60", "70", "80"],
  "Heart Rate (bpm)": ["60", "75", "90", "100"],
  "CVP (cmH2O)": ["5", "10", "15"],
  "Respiratory Rate": ["12", "16", "20"],
  "Temperature (°C)": ["36.5", "37.0", "38.0"]
};

const emptyGrid = () => allParams.map(() => Array(timeLabels.length).fill(""));

const PrimaryMonitoringGrid = forwardRef((props, ref) => {
  const [gridData, setGridData] = useState(emptyGrid());
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useImperativeHandle(ref, () => ({
    getGridData: () => gridData,
    setGridData: (data) => {
      if (Array.isArray(data) && data.length === allParams.length) {
        const newData = data.map(row =>
          Array.isArray(row) ? row.slice() : Array(timeLabels.length).fill("")
        );
        setGridData(newData);
        setTimeout(updateChart, 100);
        props.onChange?.(newData);
      }
    }
  }));

  const restrictToNumbers = (e) => {
    const allowedKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Enter", ".", "-"];
    if (!/[0-9]/.test(e.key) && !allowedKeys.includes(e.key)) {
      e.preventDefault();
    }
  };

  function updateChart() {
    if (!chartInstance.current) return;
    chartInstance.current.data.datasets.forEach((dataset, i) => {
      if (!gridData[i]) return;
      dataset.data = gridData[i].map(val => {
        const num = parseFloat(val);
        return isNaN(num) ? null : num;
      });
    });
    chartInstance.current.update();
  }

  useEffect(() => {
    if (chartRef.current && !chartInstance.current) {
      chartInstance.current = new Chart(chartRef.current, {
        type: "line",
        data: {
          labels: timeLabels,
          datasets: chartParams.map((param, i) => ({
            label: param,
            data: [],
            borderColor: `hsl(${i * 60}, 70%, 50%)`,
            fill: false,
            tension: 0.3
          }))
        },
        options: {
          responsive: true,
          interaction: { mode: "index", intersect: false },
          scales: { y: { beginAtZero: true } },
          plugins: { legend: { position: "bottom" } }
        }
      });
    }
    updateChart();
    // eslint-disable-next-line
  }, [gridData]);

  return (
    <div className="bg-white p-4 rounded shadow-md">
      <div className="overflow-x-auto">
        <table
          id="primaryMonitoringTable"
          className="min-w-full text-sm text-center border border-gray-300"
        >
          <thead className="bg-gray-100">
            <tr>
              <th className="border px-2 py-1">Parameter/Time</th>
              {timeLabels.map(time => (
                <th key={time} className="border px-2 py-1">{time}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {allParams.map((param, rowIdx) => (
              <tr key={param} data-parameter={param}>
                <td className="border font-medium bg-gray-50 px-2 py-1 text-left">{param}</td>
                {timeLabels.map((_, colIdx) => (
                  <td
                    key={colIdx}
                    className="border bg-white px-2 py-1 hover:bg-blue-50 focus-within:bg-blue-100 transition-colors"
                  >
                    <input
                      className="w-full px-1 py-1 text-sm bg-transparent outline-none"
                      type="text"
                      value={gridData[rowIdx][colIdx]}
                      list={paramSuggestions[param] ? `${param.replace(/\s+/g, '')}-list` : undefined}
                      onChange={e => {
                        const newGrid = gridData.map(row => row.slice());
                        newGrid[rowIdx][colIdx] = e.target.value;
                        setGridData(newGrid);
                        props.onChange?.(newGrid);
                      }}
                      onKeyPress={rowIdx < chartParams.length ? restrictToNumbers : undefined}
                      placeholder="–"
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Datalists for auto-suggestions */}
      {Object.entries(paramSuggestions).map(([param, values]) => (
        <datalist id={`${param.replace(/\s+/g, '')}-list`} key={param}>
          {values.map(val => <option key={val} value={val} />)}
        </datalist>
      ))}

      <div className="mt-6 text-sm text-gray-600 font-medium md:hidden">
        Live Chart (Numeric Parameters Only)
      </div>
      <canvas ref={chartRef} height="140" className="mt-2 md:hidden"></canvas>
    </div>
  );
});

export default PrimaryMonitoringGrid;
